/**
 * Mesh generation from logo outlines
 */

export function createMeshFromOutline(gl, outlineData, width, height) {
  // Handle both old format (single points array) and new format (multiple contours)
  let contours;
  if (outlineData.contours) {
    contours = outlineData.contours;
  } else if (outlineData.points) {
    contours = [outlineData.points];
  } else {
    console.error('Invalid outline data');
    return null;
  }

  if (contours.length === 0 || contours[0].length < 3) {
    console.error('Invalid outline data');
    return null;
  }

  const depth = Math.min(width, height) * 0.1;

  const vertices = [];
  const texCoords = [];
  const normals = [];
  const indices = [];

  let currentVertexOffset = 0;

  contours.forEach(points => {
    const scaledPoints = points.map(p => ({
      x: (p.x - 0.5) * width,
      y: (0.5 - p.y) * height
    }));

    const numPoints = scaledPoints.length;
    const frontStartIdx = currentVertexOffset;
    const backStartIdx = currentVertexOffset + numPoints;
    const sidesStartIdx = currentVertexOffset + numPoints * 2;

    // Front face (z = 0)
    scaledPoints.forEach((p, i) => {
      vertices.push(p.x, p.y, 0);
      texCoords.push(points[i].x, points[i].y);
      normals.push(0, 0, 1);
    });

    // Back face (z = -depth)
    scaledPoints.forEach((p, i) => {
      vertices.push(p.x, p.y, -depth);
      texCoords.push(points[i].x, points[i].y);
      normals.push(0, 0, -1);
    });

    // Create side face vertices with proper normals
    for (let i = 0; i < numPoints; i++) {
      const next = (i + 1) % numPoints;
      const p1 = scaledPoints[i];
      const p2 = scaledPoints[next];

      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const len = Math.sqrt(dx * dx + dy * dy);

      const nx = len > 0 ? -dy / len : 0;
      const ny = len > 0 ? dx / len : 0;

      // Add 4 vertices for this edge's quad
      vertices.push(p1.x, p1.y, 0);
      texCoords.push(points[i].x, points[i].y);
      normals.push(nx, ny, 0);

      vertices.push(p2.x, p2.y, 0);
      texCoords.push(points[next].x, points[next].y);
      normals.push(nx, ny, 0);

      vertices.push(p1.x, p1.y, -depth);
      texCoords.push(points[i].x, points[i].y);
      normals.push(nx, ny, 0);

      vertices.push(p2.x, p2.y, -depth);
      texCoords.push(points[next].x, points[next].y);
      normals.push(nx, ny, 0);
    }

    // Triangulate front face
    for (let i = 1; i < numPoints - 1; i++) {
      indices.push(frontStartIdx, frontStartIdx + i, frontStartIdx + i + 1);
    }

    // Triangulate back face
    for (let i = 1; i < numPoints - 1; i++) {
      indices.push(backStartIdx, backStartIdx + i + 1, backStartIdx + i);
    }

    // Create side face triangles
    for (let i = 0; i < numPoints; i++) {
      const baseIdx = sidesStartIdx + i * 4;
      indices.push(baseIdx, baseIdx + 1, baseIdx + 2);
      indices.push(baseIdx + 1, baseIdx + 3, baseIdx + 2);
    }

    currentVertexOffset += numPoints * 2 + numPoints * 4;
  });

  // Create buffers
  const vertexBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertices), gl.STATIC_DRAW);

  const texCoordBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(texCoords), gl.STATIC_DRAW);

  const normalBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, normalBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(normals), gl.STATIC_DRAW);

  const indexBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);

  return {
    vertexBuffer,
    texCoordBuffer,
    normalBuffer,
    indexBuffer,
    indexCount: indices.length
  };
}
